import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import {
  API_URL_HOTSPOT_LANE_LABELS,
  getConfiguredApiUrlHotspotLanes,
} from "../../../api/cameraHotspots.ts";
import {
  PATROL_API_URL_BUTTON_GAP,
  PATROL_API_URL_BUTTON_SIZE,
  PATROL_API_URL_GREEN_ICON,
  PATROL_API_URL_RED_ICON,
  PATROL_API_URL_YELLOW_ICON,
  parseApiUrlHotspotContent,
  triggerPatrolApiUrlHotspotUrl,
} from "./patrolApiUrlHotspot.js";

const LANE_ICONS = {
  green: PATROL_API_URL_GREEN_ICON,
  red: PATROL_API_URL_RED_ICON,
  yellow: PATROL_API_URL_YELLOW_ICON,
};

function ApiUrlLaneButton({ lane, iconUrl, busy, emphasized, onPress }) {
  const label = API_URL_HOTSPOT_LANE_LABELS[lane];
  const isBusy = busy;

  return (
    <Box
      component="button"
      type="button"
      disabled={isBusy}
      aria-label={`Call ${label} API`}
      aria-pressed={emphasized}
      onClick={onPress}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        border: "none",
        outline: "none",
        p: 0,
        width: PATROL_API_URL_BUTTON_SIZE,
        height: PATROL_API_URL_BUTTON_SIZE,
        borderRadius: 0,
        cursor: isBusy ? "wait" : "pointer",
        opacity: isBusy ? 0.75 : emphasized ? 1 : 0.9,
        bgcolor: "transparent",
        display: "block",
        flexShrink: 0,
        boxShadow: "none",
        transition: "transform 0.12s ease, opacity 0.12s ease",
        "&:hover:not(:disabled)": {
          transform: "scale(1.08)",
        },
      }}
    >
      <Box
        component="img"
        src={iconUrl}
        alt=""
        aria-hidden
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          bgcolor: "transparent",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}

/**
 * API URL hotspot: one button per saved signal type (green / red / yellow).
 */
export const PatrolApiUrlHotspotToggle = memo(function PatrolApiUrlHotspotToggle({
  contentUrl = "",
}) {
  const endpoints = useMemo(
    () => parseApiUrlHotspotContent(contentUrl),
    [contentUrl],
  );
  const configuredLanes = useMemo(
    () => getConfiguredApiUrlHotspotLanes(contentUrl),
    [contentUrl],
  );
  const [emphasisLane, setEmphasisLane] = useState(null);
  const [busyLane, setBusyLane] = useState(null);

  useEffect(() => {
    if (configuredLanes.length === 0) {
      setEmphasisLane(null);
      return;
    }
    if (!emphasisLane || !configuredLanes.includes(emphasisLane)) {
      setEmphasisLane(configuredLanes[0]);
    }
  }, [configuredLanes, emphasisLane]);

  const handleLaneClick = useCallback(
    async (lane, e) => {
      e.preventDefault();
      e.stopPropagation();
      const url = endpoints[lane];
      const label = API_URL_HOTSPOT_LANE_LABELS[lane];
      if (busyLane || !url) return;

      setBusyLane(lane);
      try {
        await triggerPatrolApiUrlHotspotUrl(url, label, contentUrl);
        setEmphasisLane(lane);
      } catch {
        /* toast in triggerPatrolApiUrlHotspotUrl */
      } finally {
        setBusyLane(null);
      }
    },
    [busyLane, contentUrl, endpoints],
  );

  if (configuredLanes.length === 0) {
    return null;
  }

  return (
    <Box
      className="patrol-api-url-toggle"
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: `${PATROL_API_URL_BUTTON_GAP}px`,
        lineHeight: 0,
        pointerEvents: "auto",
      }}
    >
      {configuredLanes.map((lane) => (
        <ApiUrlLaneButton
          key={lane}
          lane={lane}
          iconUrl={LANE_ICONS[lane]}
          busy={busyLane === lane}
          emphasized={emphasisLane === lane}
          onPress={(e) => void handleLaneClick(lane, e)}
        />
      ))}
      {busyLane ? (
        <CircularProgress
          size={10}
          sx={{ position: "absolute", right: -4, bottom: -4 }}
        />
      ) : null}
    </Box>
  );
});
